import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BOARD_STATUSES = ["planned", "in_progress", "fixed"];

export default function AdminFeedback() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [board, setBoard] = useState([]);
    const [newTitle, setNewTitle] = useState("");
    const [newStatus, setNewStatus] = useState("planned");

    useEffect(() => {
        if (!user) return;
        if (!user.is_admin) {
            setLoading(false);
            return;
        }
        Promise.all([
            api.get("/admin/feedbacks?limit=200").then((r) => setRows(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "load failed")),
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
            api.get("/board").then((r) => setBoard(r.data)).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, [user]);

    const addBoardItem = async () => {
        if (newTitle.trim().length < 2) return;
        try {
            const r = await api.post("/admin/board", { title: newTitle.trim(), status: newStatus });
            setBoard((prev) => [{ ...r.data, votes: 0, voted: false }, ...prev]);
            setNewTitle("");
            toast.success("added to the board");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "couldn't add");
        }
    };

    const setItemStatus = async (id, status) => {
        try {
            await api.patch(`/admin/board/${id}`, { status });
            setBoard((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
        } catch {
            toast.error("couldn't update");
        }
    };

    const removeItem = async (id) => {
        try {
            await api.delete(`/admin/board/${id}`);
            setBoard((prev) => prev.filter((it) => it.id !== id));
        } catch {
            toast.error("couldn't delete");
        }
    };

    if (loading) {
        return <div className="max-w-4xl mx-auto pt-10 text-inkSoft font-body">loading…</div>;
    }

    if (!user?.is_admin) {
        return (
            <div className="max-w-md mx-auto pt-20 text-center">
                <h1 className="font-heading text-3xl text-ink mb-4">not for you</h1>
                <p className="text-inkSoft font-body">this page is only visible to admins.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto pt-4">
                <p className="text-xs uppercase tracking-[0.3em] text-inkSoft mb-3 font-body">admin</p>
                <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-ink mb-8">
                    feedback & stats
                </h1>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10" data-testid="admin-stats">
                        {Object.entries(stats).map(([k, v]) => (
                            <div key={k} className="bg-white/70 border border-clayHover rounded-2xl p-4">
                                <p className="text-xs uppercase tracking-widest text-inkSoft mb-1 font-body">{k.replace(/_/g, " ")}</p>
                                <p className="font-heading text-2xl tracking-tight text-ink">{v}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mb-12" data-testid="admin-board">
                    <h2 className="font-heading text-2xl tracking-tight text-ink mb-4">beta board</h2>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <input
                            data-testid="admin-board-title"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="what are you fixing / building?"
                            className="flex-1 min-w-[220px] bg-white border border-clayHover rounded-xl px-4 py-2.5 font-body text-sm outline-none focus:border-terracotta"
                        />
                        <select
                            data-testid="admin-board-status"
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                            className="bg-white border border-clayHover rounded-xl px-3 py-2.5 font-body text-sm outline-none"
                        >
                            {BOARD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>
                        <button
                            data-testid="admin-board-add"
                            onClick={addBoardItem}
                            className="bg-terracotta text-bone rounded-full p-2.5 hover:bg-terracottaHover transition-colors"
                            aria-label="Add board item"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="space-y-2">
                        {board.map((it) => (
                            <div key={it.id} className="flex items-center gap-3 bg-white/70 border border-clayHover rounded-2xl px-4 py-3 font-body text-sm">
                                <span className="text-inkSoft w-10 text-center">▲{it.votes}</span>
                                <span className="flex-1 text-ink">{it.title}</span>
                                <select
                                    value={it.status}
                                    onChange={(e) => setItemStatus(it.id, e.target.value)}
                                    className="bg-clay/50 border border-clayHover rounded-lg px-2 py-1 text-xs outline-none"
                                >
                                    {BOARD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                </select>
                                <button onClick={() => removeItem(it.id)} className="text-inkSoft hover:text-red-500" aria-label="Delete item">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-3" data-testid="admin-feedback-list">
                    {rows.length === 0 && (
                        <p className="text-inkSoft italic font-body">no feedback yet.</p>
                    )}
                    {rows.map((f) => (
                        <div key={f.id} className="bg-white/70 border border-clayHover rounded-2xl p-5">
                            <div className="flex items-center gap-3 mb-2 text-xs text-inkSoft font-body">
                                <span>{f.user_email || f.user_id?.slice(0, 8)}</span>
                                <span>·</span>
                                <span>{new Date(f.created_at).toLocaleString()}</span>
                                <span>·</span>
                                <span>{f.context}</span>
                                {f.rating && (
                                    <span className="inline-flex items-center gap-0.5 ml-auto">
                                        {Array.from({ length: f.rating }).map((_, i) => (
                                            <Star key={i} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
                                        ))}
                                    </span>
                                )}
                            </div>
                            <p className="font-body text-ink whitespace-pre-wrap">{f.message}</p>
                        </div>
                    ))}
                </div>
            </div>
    );
}
